import { format } from 'date-fns';

import {
  ADD_DAYS_URL_PARAM,
  AGE_URL_PARAM,
  CONSUMER_CATEGORY_URL_PARAM,
  DATE_URL_PARAM,
  DAY_OF_WEEK_URL_PARAM,
  PARTICIPANTS_URL_PARAM,
  PRODUCT_CATEGORY_URL_PARAM,
  SELECTOR_MODE,
  STUDENT_URL_PARAM,
} from '~constants';
import { ParticipantModeHelper } from '~helpers';
import { ConsumerCategory } from '~types/appsync.types';
import { Participant } from '~types/participant';

import { parseCalendarDate } from './date';

export const getUrlAddToCart = (
  websiteUrl?: string,
  productSlug?: string,
  date?: Date,
  participants: Participant[] = [],
  locale?: string,
) => {
  if (!websiteUrl || !productSlug || !date || !participants.length || !locale) {
    return '';
  }
  const participantsParameters = participants
    .map((participant) => {
      let params = `sk[${participant.id}][age]=${participant.age}`;

      if (participant.consumerCategory.student) {
        params = `${params}&sk[${participant.id}][stud]=1`;
      }

      if (participant.timeslotExternalId) {
        params = `${params}&sk[${participant.id}][subcontingent]=${participant.timeslotExternalId}`;
      }

      const options = participant.options
        ?.map((option) => {
          let optionParam = `sk[${participant.id}][opts][${option.slug}]=1`;

          if (option.isBooking && option.timeslot?.externalId) {
            optionParam += `&sk[${participant.id}][subcontingent_opts][${option.slug}]=${option.timeslot.externalId}`;
          }

          return optionParam;
        })
        .join('&');

      if (options) {
        params = `${params}&${options}`;
      }
      return params;
    })
    .join('&');
  const formattedStartDate = format(date, 'yyyy-MM-dd');

  return `${websiteUrl}/${locale}/cart/${productSlug}/merge?s=${formattedStartDate}&${participantsParameters}`;
};

export const normalizeUrl = (
  searchParams: URLSearchParams,
  consumerCategories: ConsumerCategory[],
  participantsLimit?: number,
) => {
  const participantsParams = readParticipantsParams(searchParams);
  const mode = ParticipantModeHelper.getMode();

  const normalized: ParticipantParams[] = [];

  const normalizeAgeMode = (p: ParticipantParams): ParticipantParams | undefined => {
    if (typeof p.age === 'number') return { age: p.age, stud: p.stud };
    if (p.conscat) {
      const cat = consumerCategories.find((cc) => cc.shortname === p.conscat);
      if (cat) return { age: getMedianAge(cat), stud: p.stud };
    }
    return undefined;
  };

  const normalizeCategoryMode = (p: ParticipantParams): ParticipantParams | undefined => {
    if (p.conscat) return { conscat: p.conscat };
    if (typeof p.age === 'number') {
      const cat = findCategoryByAge(p.age, consumerCategories, p.stud ? true : undefined);
      if (cat) return { conscat: cat.shortname };
    }
    return undefined;
  };

  for (const p of participantsParams) {
    const out = mode === SELECTOR_MODE.AGE ? normalizeAgeMode(p) : normalizeCategoryMode(p);
    if (out) normalized.push(out);
  }

  writeParticipantsParams(
    typeof participantsLimit === 'number' ? normalized.slice(0, participantsLimit) : normalized,
  );
};

export const parseParticipantsFromUrl = (
  searchParams: URLSearchParams,
  consumerCategories: ConsumerCategory[],
): Participant[] => {
  const participantsParams = readParticipantsParams(searchParams);

  const participants: Participant[] = [];
  for (const p of participantsParams) {
    if (typeof p.age === 'number') {
      const cat = p.stud
        ? findCategoryByAge(p.age, consumerCategories, true)
        : findCategoryByAge(p.age, consumerCategories);
      if (cat)
        participants.push({
          age: p.age,
          consumerCategory: cat,
          id: participants.length + 1,
          options: [],
        });
    } else if (p.conscat) {
      const cat = consumerCategories.find((cc) => cc.shortname === p.conscat);
      if (cat)
        participants.push({
          age: getMedianAge(cat),
          consumerCategory: cat,
          id: participants.length + 1,
          options: [],
        });
    }
  }
  return participants;
};

export const parseDateFromUrl = (params: URLSearchParams, now: Date): Date | undefined => {
  const date = params.get(DATE_URL_PARAM);
  if (date && !Number.isNaN(new Date(date).getTime())) return parseCalendarDate(date);
  const addDays = Number.parseInt(params.get(ADD_DAYS_URL_PARAM) ?? '', 10);
  if (addDays > 0) {
    const dt = new Date(now);
    dt.setDate(dt.getDate() + addDays);
    return dt;
  }
  const dow = Number.parseInt(params.get(DAY_OF_WEEK_URL_PARAM) ?? '', 10);
  if (dow >= 0 && dow <= 6) {
    const dt = new Date(now);
    const diff = (dow - dt.getDay() + 7) % 7 || 7;
    dt.setDate(dt.getDate() + diff);
    return dt;
  }
};

export const parseProductCategoryFromUrl = (params: URLSearchParams): string | undefined => {
  return params.get(PRODUCT_CATEGORY_URL_PARAM) ?? undefined;
};

export const updateUrlParam = (key: string, value: string | string[] | undefined) => {
  const url = new URL(globalThis.location.href);
  const searchParams = url.searchParams;

  searchParams.delete(key);
  if (Array.isArray(value)) {
    for (const val of value) searchParams.append(key, val);
  } else if (value !== undefined) {
    searchParams.set(key, value);
  }

  const newUrl = `${url.pathname}?${searchParams.toString()}`;
  globalThis.history.replaceState(null, '', newUrl);
};

// Public helper: set p[i][age|conscat] (+ stud) from current participants and mode
export const setParticipantsInUrl = (participants: Participant[], mode: SELECTOR_MODE) => {
  const mapped: ParticipantParams[] = participants.map((p) =>
    mode === SELECTOR_MODE.AGE
      ? { age: p.age, stud: p.consumerCategory.student === true }
      : { conscat: p.consumerCategory.shortname },
  );
  writeParticipantsParams(mapped);
};

// Helpers for participants params p[i][...]
const PARTICIPANT_KEY_REGEX = new RegExp(`^${PARTICIPANTS_URL_PARAM}\\[(\\d+)\\]\\[(\\w+)\\]$`);

type ParticipantParams = { age?: number; conscat?: string; stud?: boolean };

// Read and group URL participant params p[i][age|conscat|stud] by index, ordered ascending
const readParticipantsParams = (searchParams: URLSearchParams): ParticipantParams[] => {
  const participantsByIndex = new Map<number, ParticipantParams>();
  for (const [paramKey, paramValue] of searchParams.entries()) {
    const match = PARTICIPANT_KEY_REGEX.exec(paramKey);
    if (!match) continue;
    const index = Number.parseInt(match[1], 10);
    const field = match[2];
    const participantAtIndex = participantsByIndex.get(index) ?? {};

    if (field === AGE_URL_PARAM) {
      const age = Number.parseInt(paramValue, 10);
      if (!Number.isNaN(age) && age > 0) participantAtIndex.age = age;
    } else if (field === CONSUMER_CATEGORY_URL_PARAM) {
      participantAtIndex.conscat = paramValue;
    } else if (field === STUDENT_URL_PARAM) {
      participantAtIndex.stud = paramValue === '1';
    }

    participantsByIndex.set(index, participantAtIndex);
  }

  return [...participantsByIndex.keys()]
    .sort((a, b) => a - b)
    .map((k) => participantsByIndex.get(k)!);
};

const writeParticipantsParams = (participants: ParticipantParams[]) => {
  const url = new URL(globalThis.location.href);
  const searchParams = url.searchParams;
  // Remove existing p[...] keys
  const toDelete: string[] = [];
  for (const key of Array.from(searchParams.keys())) {
    if (PARTICIPANT_KEY_REGEX.test(key)) toDelete.push(key);
  }
  for (const k of toDelete) searchParams.delete(k);

  // Append new compacted indices starting at 1
  for (let i = 0; i < participants.length; i++) {
    const participant = participants[i];
    const idx = i + 1;
    if (typeof participant.age === 'number') {
      searchParams.append(
        `${PARTICIPANTS_URL_PARAM}[${idx}][${AGE_URL_PARAM}]`,
        String(participant.age),
      );
      if (participant.stud)
        searchParams.append(`${PARTICIPANTS_URL_PARAM}[${idx}][${STUDENT_URL_PARAM}]`, '1');
    } else if (participant.conscat) {
      searchParams.append(
        `${PARTICIPANTS_URL_PARAM}[${idx}][${CONSUMER_CATEGORY_URL_PARAM}]`,
        participant.conscat,
      );
    }
  }

  globalThis.history.replaceState(null, '', `${url.pathname}?${searchParams.toString()}`);
};

const getMedianAge = (cc: ConsumerCategory) => Math.floor((cc.ageMin + cc.ageMax) / 2);

const findCategoryByAge = (age: number, list: ConsumerCategory[], isStudent: boolean = false) => {
  return list.find((cc) => cc.ageMin <= age && cc.ageMax >= age && cc.student === isStudent);
};
